/**
 * AddEventSheet.tsx — "+ Add event", from a tapped day. #1957 item 2.
 *
 * Reached from the day sheet (month view) and from a tapped day header (week
 * view). The date is prefilled to the day she tapped, which is the whole reason
 * this is a per-day entry point rather than a global "new event" button.
 *
 * ── Why it saves through writeKinEvents ─────────────────────────────────────
 *
 * Because that is what reaches her Apple Calendar. `kin-calendar-feed` serves
 * the `events` table and nothing else, so an event written anywhere else is an
 * event that never syncs — and the failure is invisible until she looks at her
 * phone. Saving through the same writer Snap-a-Schedule uses means the mirror
 * row, the category stamp and the dedup-friendly row shape all come for free
 * instead of being re-derived here and drifting.
 *
 * ── What "Who's it for" actually sets ───────────────────────────────────────
 *
 * The calendar colors by `events.child` — a kid's NAME, matched against the
 * legend's color map. There is no color column. So:
 *
 *   a kid   → child = "<name>",  source_type = "manual"
 *   Family  → child = null,      source_type = "manual"       (periwinkle)
 *   Work    → child = null,      source_type = "work_calendar" (slate)
 *
 * Work is a source_type rather than a child because that is the rule the dots
 * and bars already read (`eventPersonColor` in TodayTab). Inventing a child
 * named "Work" would put a fake kid in her legend.
 *
 * ⚠️ `source_type` on `imported_events` has a CHECK constraint —
 * ('schedule','newsletter','work_calendar','manual'). "manual" and
 * "work_calendar" are both inside it; anything else 23514s the save.
 *
 * ── Recurrence is deliberately absent ──────────────────────────────────────
 *
 * The `events` table does support repeats, but not as a field on a row: a rule
 * lives in `recurrences` and is MATERIALIZED into one events row per
 * occurrence by a weekly cron. So "repeat weekly" is not a checkbox this form
 * could tick — it is a different write to a different table. Typing "swim
 * every Tuesday at 4:45" into the Today box already builds one.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useGoogleCalendar } from "@/hooks/useGoogleCalendar";
import { writeKinEvents, type KinEventDraft } from "@/lib/kinEventWrite";
import { saveRecurrence } from "@/lib/recurrenceWrite";
import { REMINDER_OPTIONS, reminderMinutesOf, type ReminderValue } from "@/lib/reminderOptions";
import EventTextMeToggle, { TEXT_ME_DEFAULT_OFFSET } from "@/components/EventTextMeToggle";
import { canTextHer, saveEventTextMe } from "@/lib/eventTextMeSave";
import { buildEventTextMeRow } from "@/lib/eventTextMeReminder";
import { textMeRefusedToast, textMeSavedToast, textMeUnavailableNote } from "@/lib/eventTextMeCopy";
import { usePreferences } from "@/hooks/useUserPreferences";
import { useAuth } from "@/hooks/useAuth";
import { useIsPro } from "@/hooks/useIsPro";
import { reportError } from "@/lib/reportError";
import { trackFeature } from "@/lib/analytics";

/** The two non-kid chips. Values that can never collide with a kid's name. */
import { FAMILY_CHIP, WORK_CHIP } from "@/lib/eventWhoChips";
export { FAMILY_CHIP, WORK_CHIP };

/**
 * Repeat options — EXACTLY what Kin's recurrence model can produce.
 *
 * ⚠️ A repeat is a rule row in `recurrences` plus materialized rows in
 * `events`, and the rule is WEEKLY BY DAY-OF-WEEK with an optional end date.
 * There is no monthly, no yearly and no every-N-weeks, so offering one would
 * promise something the materializer cannot build. `day_of_week` is an int[],
 * which is the only reason "every weekday" is available here.
 *
 * "weekly" resolves against the DATE she picked, not against today — a Tuesday
 * event repeats on Tuesdays.
 */
export type RepeatMode = "none" | "weekly" | "weekdays";

const WEEKDAY_NAMES = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
];

/** 0=Sun..6=Sat for a "YYYY-MM-DD", or null when unparseable. */
function weekdayOf(iso: string): number | null {
  const d = new Date(`${iso}T00:00:00`);
  return isNaN(d.getTime()) ? null : d.getDay();
}

/** "every Tuesday" / "every weekday" — for the confirmation and the picker. */
export function repeatLabel(mode: RepeatMode, iso: string): string {
  if (mode === "weekdays") return "every weekday";
  if (mode === "weekly") {
    const dow = weekdayOf(iso);
    return dow === null ? "weekly" : `every ${WEEKDAY_NAMES[dow]}`;
  }
  return "";
}

export interface AddEventSheetProps {
  /** "YYYY-MM-DD" — the day she tapped. Prefills the date field. */
  date: string;
  userId: string;
  /** Kid names from the family profile (known_kid_names). */
  kids: string[];
  /** Persisted kid colors, so a chip shows the color the calendar will use. */
  kidColors?: Record<string, string>;
  onClose: () => void;
  /** Called after a save that landed, so the caller can re-read the month. */
  onSaved: (savedDate: string) => void;
}

export const FAMILY_COLOR = "#5050C8";
export const WORK_COLOR = "#54525C";
export const FALLBACK_KID_COLORS = ["#E8A33D", "#3DA5A5", "#C85A9B", "#6B8E3D", "#A3561F"];

/** "14:30" → "14:30:00" for Postgres `time`. Empty stays null. */
function toPgTime(value: string): string | null {
  return /^\d{2}:\d{2}$/.test(value) ? `${value}:00` : null;
}

/** "2026-11-20" → "Friday, November 20" for the confirmation. */
function longDateLabel(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
}

export const LABEL_STYLE: React.CSSProperties = {
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  fontSize: 11,
  fontWeight: 700,
  color: "#54525C",
  letterSpacing: "0.03em",
  textTransform: "uppercase",
  display: "block",
  marginBottom: 6,
};

export const FIELD_STYLE: React.CSSProperties = {
  width: "100%",
  minHeight: 44,
  padding: "10px 12px",
  borderRadius: 12,
  border: "1.5px solid #C8C8F4",
  background: "#FFFFFF",
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  fontSize: 15,
  color: "#1C1A3E",
  boxSizing: "border-box",
  // #ios — a DATE/TIME input renders as a grey native pill without this, and
  // its text is unreadable against the sheet.
  WebkitAppearance: "none",
  appearance: "none",
};

const AddEventSheet = ({
  date,
  userId,
  kids,
  kidColors,
  onClose,
  onSaved,
}: AddEventSheetProps) => {
  const [title, setTitle] = useState("");
  const [eventDate, setEventDate] = useState(date);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [allDay, setAllDay] = useState(false);
  const [repeat, setRepeat] = useState<RepeatMode>("none");
  const [who, setWho] = useState<string>(FAMILY_CHIP);
  const [reminder, setReminder] = useState<ReminderValue>("none");
  // #1966 her item 11 — "💬 Text me about this one". Separate from the
  // calendar reminder above ON PURPOSE: one is an alarm her calendar app
  // raises, the other is a text Kin sends, and conflating them is what made
  // the old "Remind me" label a lie.
  const [textMe, setTextMe] = useState(false);
  const [textMeOffset, setTextMeOffset] = useState<ReminderValue>(TEXT_ME_DEFAULT_OFFSET);
  const [location, setLocation] = useState("");
  const [saving, setSaving] = useState(false);
  const titleRef = useRef<HTMLInputElement | null>(null);
  const { connected: googleConnected, addEvents } = useGoogleCalendar();
  const { prefs } = usePreferences();
  const { isAdmin, adminPreview, proLoaded } = useAuth();
  const isPro = useIsPro();
  // ⚠️ The #668/#616 idiom, verbatim: isProTier so an admin with preview ON
  // can reach it, and `proLoaded &&` in front so the note does not flash at a
  // paying user during the entitlements fetch.
  const isProTier = isPro || (isAdmin && adminPreview);
  const proBlocked = proLoaded && !isProTier;

  // The timezone the RUNNER gates on. Not the device zone and not
  // profiles.timezone: run-scheduled-tasks builds its map from
  // notifications_timezone alone and fails closed without it, so scheduling
  // against anything else writes a row that cannot be delivered.
  const notificationsTz = prefs?.notifications_timezone ?? null;

  // ⚠️ Copilot on #1983 — Pro gates the WRITE and says nothing about delivery.
  // send-sms skips an account with no verified phone or with STOP set, so
  // without this she could be told "Kin will text you at 1:00 PM" about a text
  // that can never be sent.
  const textMeNoPhone = !proBlocked && !canTextHer(prefs);

  /* ⚠️ Copilot on #1983 — A REPEATING EVENT IS A DIFFERENT WRITE, and the
   * save path for it RETURNS before saveEventTextMe is ever reached (see the
   * `repeat !== "none"` branch below). So this toggle used to stay enabled on
   * a weekly event, save "successfully", and produce no reminder and no
   * warning — the silent-save failure the Pro gate and the read-back both
   * exist to prevent, arriving by a third route neither of them watches.
   *
   * Disabled with a line rather than silently ignored. "One row per event" has
   * nothing single to attach to when the event is a RULE plus a materialized
   * occurrence per week; `recurring_reminder` is the task type that answers
   * "remind me every Tuesday", and routing to it is its own piece of work. */
  const textMeRepeatBlocked = repeat !== "none";
  useEffect(() => {
    // Clear a selection she made BEFORE switching Repeat on, so the state
    // cannot disagree with the disabled control. Without this, ticking the box
    // and then choosing Weekly leaves `textMe` true and unreachable.
    if (textMeRepeatBlocked && textMe) setTextMe(false);
  }, [textMeRepeatBlocked, textMe]);

  /** What the toggle shows BEFORE she saves: the real fire time, or why there
   *  isn't one. Computed from the same builder the save uses, so the preview
   *  and the row cannot disagree. */
  const textMePreview = useMemo(() => {
    if (!textMe || proBlocked || textMeNoPhone || textMeRepeatBlocked) return null;
    const mins = reminderMinutesOf(textMeOffset);
    if (mins === null) return null;
    return buildEventTextMeRow({
      userId,
      // The id is irrelevant to placement and the row is thrown away; only the
      // time matters here.
      eventId: "00000000-0000-0000-0000-000000000000",
      title: title.trim() || "your event",
      eventDate,
      startTime: allDay ? null : (startTime || null),
      isAllDay: allDay,
      offsetMinutes: mins,
      timeZone: notificationsTz,
      nowIso: new Date().toISOString(),
    });
  }, [textMe, proBlocked, textMeNoPhone, textMeRepeatBlocked, textMeOffset, userId, title, eventDate, startTime, allDay, notificationsTz]);

  // Re-prefill if she closes and taps a different day without unmounting.
  useEffect(() => setEventDate(date), [date]);

  const chips = useMemo(
    () => [
      ...kids.map((name, i) => ({
        key: name,
        label: name,
        color:
          kidColors?.[name] ??
          FALLBACK_KID_COLORS[i % FALLBACK_KID_COLORS.length],
      })),
      { key: FAMILY_CHIP, label: "Family", color: FAMILY_COLOR },
      { key: WORK_CHIP, label: "Work", color: WORK_COLOR },
    ],
    [kids, kidColors],
  );

  const handleSave = async () => {
    const cleanTitle = title.trim();
    if (!cleanTitle) {
      toast.error("Give it a name first 💜");
      titleRef.current?.focus();
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(eventDate)) {
      toast.error("Pick a date for it 💜");
      return;
    }
    // An end before the start is a typo, not a plan. Caught here rather than
    // written and quietly rendered backwards.
    if (!allDay && startTime && endTime && endTime < startTime) {
      toast.error("That ends before it starts — check the times 💜");
      return;
    }

    const repeatDays = (() => {
      if (repeat === "weekdays") return [1, 2, 3, 4, 5];
      if (repeat === "weekly") {
        const dow = weekdayOf(eventDate);
        return dow === null ? [] : [dow];
      }
      return [];
    })();
    if (repeat !== "none" && repeatDays.length === 0) {
      toast.error("Pick a date for it 💜");
      return;
    }

    setSaving(true);
    const isWork = who === WORK_CHIP;
    const child = who === FAMILY_CHIP || isWork ? null : who;

    // ── A repeating event is a DIFFERENT write ──────────────────────────────
    //
    // Not an events row with a flag on it — there is no such field. It is a
    // rule in `recurrences` plus one materialized events row per occurrence,
    // and `saveRecurrence` is the same function RecurrenceChip uses, so
    // "swim every Tuesday at 4:45" typed into the Today box and this form
    // produce identical rows.
    //
    // ⚠️ It therefore does NOT go through writeKinEvents, so it writes no
    // imported_events row and there is nothing for the Apple .ics handoff to
    // address. The occurrences still reach her Apple Calendar: the Kin feed
    // serves the `events` table, which is exactly what this materializes into.
    if (repeat !== "none") {
      const { error: recErr, alreadyExisted, materialized } = await saveRecurrence(userId, {
        title: cleanTitle,
        dayOfWeek: repeatDays,
        startTime24: allDay ? null : (toPgTime(startTime) ?? "").slice(0, 5) || null,
        // ⚠️ #607 — the field that separates all-day from TBD. startTime24 is
        // null for both, so it cannot decide.
        startTimeLabel: allDay ? "all-day" : startTime ? startTime : "TBD",
        endTime24: allDay ? null : (toPgTime(endTime) ?? "").slice(0, 5) || null,
        child,
        location: location.trim() || null,
        startDate: eventDate,
        untilDate: null,
      });
      if (recErr) {
        reportError("[AddEventSheet] recurrence save failed", recErr, {
          tags: { area: "add-event" },
          extra: { date: eventDate, repeat },
        });
        toast.error("Couldn't save that repeat — give it another tap 💜");
        setSaving(false);
        return;
      }
      trackFeature("today_event_add", { method: "add_event_sheet", repeat });
      toast(
        alreadyExisted
          ? `${cleanTitle} already repeats like that 💜`
          : `Added ${cleanTitle}, repeating ${repeatLabel(repeat, eventDate)} 💜`,
        { duration: 3500 },
      );
      setSaving(false);
      onSaved(eventDate);
      onClose();
      return;
    }

    const draft: KinEventDraft = {
      title: cleanTitle,
      event_date: eventDate,
      start_time: allDay ? null : toPgTime(startTime),
      end_time: allDay ? null : toPgTime(endTime),
      is_all_day: allDay,
      location: location.trim() || null,
      notes: null,
      child,
    };

    const batchId = crypto.randomUUID();
    // #1966 — the reminder is STORED now, so it survives this save and reaches
    // the subscribed Apple calendar and any later Google push. Before this it
    // existed only as an argument to pushToGoogle below.
    const reminderMinutes = reminderMinutesOf(reminder);
    const { insertedIds, error, mirrorError } = await writeKinEvents({
      userId,
      drafts: [draft],
      sourceType: isWork ? "work_calendar" : "manual",
      batchId,
      reminderMinutes,
    });

    if (error || insertedIds.length === 0) {
      reportError("[AddEventSheet] save failed", error, {
        tags: { area: "add-event" },
        extra: { date: eventDate },
      });
      toast.error("Couldn't save that one — give it another tap 💜");
      setSaving(false);
      return;
    }

    // ⚠️ The mirror is what her week and her Apple Calendar read. If only the
    // imported_events row landed, the event IS saved but will not show up, so
    // this must not report a plain success (house rule 16).
    if (mirrorError) {
      toast.error("Saved, but it may not show on your calendar yet — tell Kasandra 💜", {
        duration: 6000,
      });
      setSaving(false);
      onSaved(eventDate);
      onClose();
      return;
    }

    // ⚠️ #1966 her item 11 — the text-me row, AFTER the event has landed and
    // only when she asked for one.
    //
    // Ordering: the event first, always. The reminder's FK points at
    // `events.id`, so there is nothing to point at until the event exists —
    // and if this half fails she still has her event, which is the thing she
    // came here to save. The inverse order would risk an orphan reminder about
    // an event that was never written.
    //
    // ⚠️ `insertedIds[0]` is BOTH tables' id. writeKinEvents generates one id
    // per draft and spreads it into the `events` mirror too (#1806), so the
    // two rows share it. That is what makes this safe; a row that arrived by
    // any OTHER path has different ids in the two tables and must be addressed
    // by `events_row_id`.
    // ⚠️ The same three conditions the control is disabled on, restated here
    // rather than trusted. The toggle being disabled is a UI fact; this is the
    // write, and `repeat !== "none"` never reaches this line anyway because
    // that branch returns above — which is exactly why the control must be
    // disabled rather than this guard being considered sufficient.
    if (textMe && !proBlocked && !textMeNoPhone && !textMeRepeatBlocked) {
      const mins = reminderMinutesOf(textMeOffset);
      try {
        const saved = await saveEventTextMe({
          userId,
          eventId: insertedIds[0],
          title: cleanTitle,
          eventDate,
          startTime: allDay ? null : (draft.start_time || null),
          isAllDay: allDay,
          offsetMinutes: mins ?? 0,
          timeZone: notificationsTz,
          nowIso: new Date().toISOString(),
        });
        // ⚠️ `=== false` and the failure branch FIRST. strictNullChecks is off
        // (CLAUDE.md: intentionally loose), so `if (saved.ok)` compiles and
        // then cannot see `saved.kind` — a bare truthiness test does not
        // narrow a boolean-literal discriminant without it.
        if (saved.ok === false) {
          if (saved.kind === "write_refused") {
            // Zero rows back. RLS is the likeliest cause, and `proBlocked` is
            // what decides whether to say so — it is FALSE while entitlements
            // load, and blaming the tier for a network blip sends her to the
            // upgrade page over nothing.
            toast.error(textMeRefusedToast(proBlocked));
          } else {
            // Nothing was written because nothing COULD be. Say which.
            toast.error(textMeUnavailableNote(saved.reason));
          }
        } else {
          toast.success(textMeSavedToast(saved.localTime, saved.snapped), { duration: 5000 });
        }
      } catch (e) {
        reportError("[AddEventSheet] text-me reminder failed", e, {
          tags: { area: "event-text-me" },
          extra: { date: eventDate },
        });
        toast.error(textMeRefusedToast(proBlocked));
      }
    }

    // Google gets it too, when she's connected — the same push the Schedule
    // Import card makes, so a reminder behaves identically on both paths. The
    // SAME value that went into the row above, so the two cannot disagree.
    void pushToGoogle(draft, reminderMinutes).catch(() => {});

    // ⚠️ #1957 item A's "connect Google or you get no alert" toast is DELETED
    // here, and its deletion is the point of #1966 rather than a tidy-up. That
    // copy was true only because the value was being thrown away: nothing
    // stored it, so nothing but a live Google push could carry it. It is
    // stored now, the subscribed Apple feed emits it as a VALARM, and a Google
    // push days later reads it off the row — so the warning would be telling
    // her a reminder is lost at the moment it finally is not.

    trackFeature("today_event_add", { method: "add_event_sheet", all_day: allDay });
    toast(`Added ${cleanTitle} to ${longDateLabel(eventDate)} 💜`, { duration: 3500 });
    setSaving(false);
    onSaved(eventDate);
    onClose();
  };

  /** Best-effort Google push, through the SAME `addEvents` the Schedule
   *  Import card uses — so a reminder becomes the same `reminders.overrides`
   *  popup on both paths rather than a second payload shape.
   *
   *  ⚠️ Never blocks or fails the save. The event is already in Kin; Google is
   *  a mirror of it, not the record. But it must also never CLAIM to have
   *  pushed — `addEvents` throws on a dead token, and that is swallowed here
   *  deliberately because the success toast has already told her the truth
   *  ("Added … to <date>"), which is a claim about Kin and not about Google. */
  const pushToGoogle = async (draft: KinEventDraft, minutes: number | null) => {
    if (!googleConnected) return;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
    const startClock = draft.start_time ? draft.start_time.slice(0, 5) : "09:00";
    const start = `${draft.event_date}T${startClock}:00`;
    const endClock = draft.end_time ? draft.end_time.slice(0, 5) : null;
    await addEvents([
      {
        title: draft.title,
        start_datetime: start,
        end_datetime: endClock ? `${draft.event_date}T${endClock}:00` : start,
        // buildGoogleEvent reads this to emit a DATE-valued all-day banner
        // instead of a 9:00 block.
        all_day: draft.is_all_day,
        location: draft.location || "",
        timezone: tz,
        notes: "",
        ...(minutes !== null ? { reminder_minutes: minutes } : {}),
      },
    ] as any);
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(28,26,62,0.45)", zIndex: 220 }}
      />
      <div
        data-kin-add-event-sheet
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 221,
          background: "#F0F0FF",
          borderRadius: "20px 20px 0 0",
          maxHeight: "90vh",
          overflowY: "auto",
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 8px" }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: "#C8C8F4" }} />
        </div>
        <div style={{ padding: "0 16px" }}>
          <p
            style={{
              fontFamily: "'DM Serif Display', serif",
              fontSize: 21,
              color: "#1C1A3E",
              margin: "0 0 14px",
            }}
          >
            Add an event
          </p>

          {/* 1 — Title */}
          <div style={{ marginBottom: 14 }}>
            <label style={LABEL_STYLE} htmlFor="kin-add-title">Title</label>
            <input
              id="kin-add-title"
              ref={titleRef}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Soccer practice"
              style={FIELD_STYLE}
              // A field where Enter means "save" needs autocorrect off on iOS,
              // or the return key accepts a suggestion and the handler never
              // fires (see CLAUDE.md).
              enterKeyHint="done"
              autoCorrect="off"
              spellCheck={false}
            />
          </div>

          {/* 2 — Date */}
          <div style={{ marginBottom: 14 }}>
            <label style={LABEL_STYLE} htmlFor="kin-add-date">Date</label>
            <input
              id="kin-add-date"
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              style={FIELD_STYLE}
            />
          </div>

          {/* 3 — Start / End time, hidden by the all-day toggle below */}
          {!allDay && (
            <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={LABEL_STYLE} htmlFor="kin-add-start">Starts</label>
                <input
                  id="kin-add-start"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  style={FIELD_STYLE}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={LABEL_STYLE} htmlFor="kin-add-end">Ends</label>
                <input
                  id="kin-add-end"
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  style={FIELD_STYLE}
                />
              </div>
            </div>
          )}

          {/* 4 — All day */}
          <button
            type="button"
            onClick={() => setAllDay((v) => !v)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              minHeight: 44,
              padding: "10px 12px",
              marginBottom: 14,
              borderRadius: 12,
              border: "1.5px solid #C8C8F4",
              background: "#FFFFFF",
              cursor: "pointer",
            }}
            aria-pressed={allDay}
          >
            <span
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 15,
                color: "#1C1A3E",
                fontWeight: 600,
              }}
            >
              All day
            </span>
            <span
              style={{
                width: 44,
                height: 26,
                borderRadius: 999,
                background: allDay ? "#5050C8" : "#C8C8F4",
                position: "relative",
                flexShrink: 0,
                transition: "background 120ms linear",
              }}
            >
              <span
                style={{
                  position: "absolute",
                  top: 3,
                  left: allDay ? 21 : 3,
                  width: 20,
                  height: 20,
                  borderRadius: 999,
                  background: "#FFFFFF",
                  transition: "left 120ms linear",
                }}
              />
            </span>
          </button>

          {/* 5 — Repeat. Native <select>, like Remind me, so iOS gives it the
                 system picker. Only the options Kin's recurrence model can
                 actually produce — weekly by day-of-week — because a repeat is
                 a `recurrences` rule plus materialized rows, not a field. */}
          <div style={{ marginBottom: 14 }}>
            <label style={LABEL_STYLE} htmlFor="kin-add-repeat">Repeat</label>
            <select
              id="kin-add-repeat"
              value={repeat}
              onChange={(e) => setRepeat(e.target.value as RepeatMode)}
              style={FIELD_STYLE}
            >
              <option value="none">Doesn't repeat</option>
              <option value="weekly">{`Weekly — ${repeatLabel("weekly", eventDate)}`}</option>
              <option value="weekdays">Every weekday (Mon-Fri)</option>
            </select>
          </div>

          {/* 6 — Who's it for */}
          <div style={{ marginBottom: 14 }}>
            <label style={LABEL_STYLE}>Who's it for</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {chips.map((chip) => {
                const selected = who === chip.key;
                return (
                  <button
                    key={chip.key}
                    type="button"
                    data-kin-who-chip={chip.key}
                    onClick={() => setWho(chip.key)}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      minHeight: 44,
                      padding: "8px 14px",
                      borderRadius: 999,
                      border: selected ? `2px solid ${chip.color}` : "1.5px solid #C8C8F4",
                      background: selected ? "#FFFFFF" : "transparent",
                      cursor: "pointer",
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: 14,
                      fontWeight: selected ? 700 : 600,
                      color: "#1C1A3E",
                    }}
                  >
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 999,
                        background: chip.color,
                        display: "inline-block",
                        flexShrink: 0,
                      }}
                    />
                    {chip.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 7 — Calendar reminder. A native <select>, so iOS gives it the
                 system picker and scrolling is the platform's job, not ours.

                 ⚠️ #1957 item A — called "Remind me" until it was traced,
                 because the value was NEVER STORED and the old name promised a
                 ping from Kin that could not come.

                 ⚠️ #1966 CHANGED HALF OF THAT. `reminder_minutes` is now a
                 column on both `events` and `imported_events`, so the value
                 persists and rides the subscribed Apple calendar as a VALARM
                 without her sending anything. What has NOT changed is that
                 nothing in dispatch-notifications reads it — Kin itself still
                 does not ping her, which is why the label stays "Calendar
                 reminder" and not "Remind me". #1979 is where Kin-side
                 reminders live. */}
          <div style={{ marginBottom: 14 }}>
            <label style={LABEL_STYLE} htmlFor="kin-add-reminder">Calendar reminder</label>
            <select
              id="kin-add-reminder"
              value={reminder}
              onChange={(e) => setReminder(e.target.value as ReminderValue)}
              style={FIELD_STYLE}
            >
              {REMINDER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <div
              className="font-body"
              data-kin-reminder-helper
              style={{ fontSize: 11, color: "#54525C", marginTop: 5, lineHeight: 1.4 }}
            >
              Added as an alert in Google or Apple Calendar 💜
            </div>

            {/* 7b — ⚠️ #1966 her item 11, and the DISTINCTION from the picker
                   directly above is the whole point of putting them together.
                   That one is an alarm her calendar app raises; this one is a
                   text Kin sends. Naming both honestly next to each other is
                   what stops "Remind me" meaning two different things again.

                   Pro only, because the scheduled_tasks INSERT policy refuses
                   a non-Pro `timed_reminder` and returns zero rows with no
                   error. Disabled with one line for a free account — her call,
                   no upsell modal mid-save. */}
            <EventTextMeToggle
              enabled={textMe}
              offset={textMeOffset}
              onChange={({ enabled, offset }) => { setTextMe(enabled); setTextMeOffset(offset); }}
              proBlocked={proBlocked}
              noPhone={textMeNoPhone}
              repeatBlocked={textMeRepeatBlocked}
              repeatNote="Repeating events can't be texted about yet — ask Kin in chat for a repeating reminder."
              snappedLocalTime={textMePreview?.ok === true && textMePreview.snapped ? textMePreview.localTime : null}
              unavailableNote={textMePreview?.ok === false ? textMeUnavailableNote(textMePreview.reason) : null}
            />
          </div>

          {/* 8 — Location */}
          <div style={{ marginBottom: 18 }}>
            <label style={LABEL_STYLE} htmlFor="kin-add-location">Location (optional)</label>
            <input
              id="kin-add-location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Field 3, Riverside Park"
              style={FIELD_STYLE}
              autoCorrect="off"
            />
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: "0 0 auto",
                minHeight: 48,
                padding: "0 18px",
                borderRadius: 14,
                border: "1.5px solid #9090E8",
                background: "transparent",
                color: "#5050C8",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 15,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              data-kin-add-event-save
              style={{
                flex: 1,
                minHeight: 48,
                borderRadius: 14,
                border: "none",
                background: saving ? "#9090E8" : "#5050C8",
                color: "#FFFFFF",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 15,
                fontWeight: 700,
                cursor: saving ? "default" : "pointer",
              }}
            >
              {saving ? "Saving…" : "Save event"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddEventSheet;
